import React from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";

function normalizeMarkdown(text) {
  if (!text || typeof text !== "string") return "";
  let cleaned = text;

  // 1. Normalize Unicode bullet characters to standard markdown dash
  cleaned = cleaned.replace(/^([ \t]*)(?:\*{1,3}\s*)?[•●○]\s+/gm, (match) => {
    const stars = match.match(/\*{1,3}/);
    return stars ? `${stars[0]} - ` : "- ";
  });

  // 2. Asterisks wrapping across lines that start with bullet or ordered list markers
  cleaned = cleaned.replace(
    /^([ \t]*)(\*{1,3})\s*([-*+]|\d+\.)\s+([\s\S]*?)\2\s*$/gm,
    (match, indent, stars, marker, body) => {
      const lines = (marker + " " + body).split("\n");
      return lines
        .map((line) => {
          const itemMatch = line.match(/^(\s*(?:[-*+]|\d+\.)\s+)(.*)$/);
          if (itemMatch) {
            let content = itemMatch[2].trim();
            if (content.endsWith(stars)) {
              content = content.slice(0, -stars.length).trim();
            }
            return `${itemMatch[1]}${stars}${content}${stars}`;
          }
          return line;
        })
        .join("\n");
    }
  );

  // 3. Move asterisks placed before bullet dashes: '***- ' -> '- ***'
  cleaned = cleaned.replace(/^\s*(\*{1,3})\s*([-*+]|\d+\.)\s+/gm, "$2 $1");

  // 4. Ensure space around bold/italic asterisks accidentally fused to words
  cleaned = cleaned.replace(/([a-zA-Z0-9])(\*{1,3}[^*\n]+\*{1,3})/g, "$1 $2");
  cleaned = cleaned.replace(/(\*{1,3}[^*\n]+\*{1,3})([a-zA-Z0-9])/g, "$1 $2");

  // 5. Ensure a blank line before lists if preceded by normal paragraph text
  cleaned = cleaned.replace(/(^[^-*\d\s#>].*)\n([-*]\s+|\d+\.\s+)/gm, "$1\n\n$2");

  return cleaned;
}

const MarkdownRenderer = ({ content = "", className = "" }) => {
  if (!content) return null;
  const normalizedContent = normalizeMarkdown(content);

  return (
    <div
      className={`prose prose-sm dark:prose-invert max-w-none text-gray-900 dark:text-gray-100 leading-relaxed [--tw-prose-bullets:theme(colors.gray.700)] dark:[--tw-prose-bullets:theme(colors.gray.300)] break-words ${className}`}
    >
      <Markdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          // Headings with consistent margins and weights
          h1: ({ node, children, ...props }) => (
            <h1
              className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mt-4 mb-2"
              {...props}
            >
              {children}
            </h1>
          ),
          h2: ({ node, children, ...props }) => (
            <h2
              className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mt-3.5 mb-1.5"
              {...props}
            >
              {children}
            </h2>
          ),
          h3: ({ node, children, ...props }) => (
            <h3
              className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mt-3 mb-1"
              {...props}
            >
              {children}
            </h3>
          ),

          // Lists with clear bullet and numbering styling
          ul: ({ node, ...props }) => (
            <ul
              className="list-disc list-outside pl-5 my-2 space-y-1 text-gray-900 dark:text-gray-100 marker:text-gray-600 dark:marker:text-gray-300"
              {...props}
            />
          ),
          ol: ({ node, ...props }) => (
            <ol
              className="list-decimal list-outside pl-5 my-2 space-y-1 text-gray-900 dark:text-gray-100 marker:text-gray-600 dark:marker:text-gray-300"
              {...props}
            />
          ),
          li: ({ node, ...props }) => (
            <li
              className="text-gray-900 dark:text-gray-100 pl-0.5"
              {...props}
            />
          ),

          // Safe Link rendering (prevents XSS vulnerabilities like javascript: URLs)
          a: ({ node, href, children, ...props }) => {
            const isSafe = href && /^(https?:\/\/|mailto:|\/)/i.test(href);
            const safeHref = isSafe ? href : `https://${href || ""}`;

            return (
              <a
                href={safeHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00B4A6] dark:text-[#05DAC3] font-medium no-underline hover:underline"
                {...props}
              >
                {children}
              </a>
            );
          },

          // Blockquote styling
          blockquote: ({ node, children, ...props }) => (
            <blockquote
              className="border-l-4 border-[#05DAC3] pl-4 py-2 my-2 bg-teal-50/50 dark:bg-teal-950/20 rounded-r-lg text-gray-700 dark:text-gray-300 not-italic"
              {...props}
            >
              {children}
            </blockquote>
          ),

          // Responsive image rendering
          img: ({ node, src, alt, ...props }) => (
            <img
              src={src}
              alt={alt || "Content image"}
              loading="lazy"
              className="max-w-full h-auto max-h-[400px] rounded-xl border border-gray-200 dark:border-gray-700 my-3 shadow-xs object-contain block mx-auto"
              {...props}
            />
          ),
        }}
      >
        {normalizedContent}
      </Markdown>
    </div>
  );
};

export { MarkdownRenderer };
export default MarkdownRenderer;
